import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import sendRequest from '../../service/sendRequest';
import callApi from '../../service/callApi';
import { somethingWrongNotification } from '../notifier/notifierActions';
import { ASSISTANTS_CATEGORY } from '../../constants';
import { getStrategy } from './strategies';
import { assistantStrategy } from './strategies/assistantStrategy';
import { mediaStrategy } from './strategies/mediaStrategy';
import { chatsStrategy } from './strategies/chatsStrategy';
import { clearAllMedia } from '../../ai-playground/media-store/mediaStore';

const GET_PLAYGROUND_ASSISTANTS = 'aiPlayground/GET_PLAYGROUND_ASSISTANTS';
const RESET_SESSION = 'aiPlayground/RESET_SESSION';
const UPLOAD_FILE = 'aiPlayground/UPLOAD_FILE';
const PLAYGROUND_REQUEST = 'aiPlayground/PLAYGROUND_REQUEST';

const REPLY_ASSISTANT_PLAYGROUND = 'aiPlayground/REPLY_ASSISTANT_PLAYGROUND';
const REPLY_AI_CHAT_PLAYGROUND = 'aiPlayground/REPLY_AI_CHAT_PLAYGROUND';
const INIT_ASSISTANT_PLAYGROUND = 'aiPlayground/INIT_ASSISTANT_PLAYGROUND';
const INIT_CHAT_PLAYGROUND = 'aiPlayground/INIT_CHAT_PLAYGROUND';
const INIT_MEDIA_PLAYGROUND = 'aiPlayground/INIT_MEDIA_PLAYGROUND';

export const getPlaygroundAssistants = createAsyncThunk(GET_PLAYGROUND_ASSISTANTS, async (_, { rejectWithValue }) => {
  const responseAssistants = await sendRequest(() => callApi.get(`/api/assistant/find/all`), {
    onError: (e) => {
      return rejectWithValue(e);
    },
  });

  const responseChats = await sendRequest(() => callApi.get(`/api/ai/playground/ai-chats`), {
    onError: (e) => {
      return rejectWithValue(e);
    },
  });

  const aiMedia = await sendRequest(() => callApi.get(`/api/ai/playground/media/types`), {
    onError: (e) => {
      return rejectWithValue(e);
    },
  });

  const assistants = responseAssistants.data;

  const formattedAssistants = [
    ...(assistants[ASSISTANTS_CATEGORY.GENERATE_ALL] ?? []),
    ...(assistants[ASSISTANTS_CATEGORY.GENERATE_SELECTED] ?? []),
    ...(assistants[ASSISTANTS_CATEGORY.COMPRESS] ?? []),
    ...(assistants[ASSISTANTS_CATEGORY.CUSTOM] ?? []),
    ...(assistants[ASSISTANTS_CATEGORY.SONAR_AZ] ?? []),
  ];

  return { assistants: formattedAssistants, aiChats: responseChats.data, mediaTypes: aiMedia.data };
});

export const resetSession = createAsyncThunk(RESET_SESSION, async (_, { rejectWithValue, getState }) => {
  const threadId = getState().aiPlayground.threadId;
  const selectedAssistant = getState().aiPlayground.selectedAssistant;

  clearAllMedia();

  if (!threadId || !selectedAssistant) {
    return rejectWithValue(null);
  }

  const strategy = getStrategy(selectedAssistant);

  if (!strategy || !strategy.reset) {
    return rejectWithValue(new Error('Unsupported assistant type'));
  }

  try {
    const response = await strategy.reset(selectedAssistant, threadId);
    return response;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const uploadFile = createAsyncThunk(UPLOAD_FILE, async (file, { rejectWithValue }) => {
  const formData = new FormData();
  formData.append('file', file, file.name);

  const response = await sendRequest(
    () =>
      axios.request({
        url: `/api/util/parse/txt`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        method: 'POST',
        data: formData,
      }),
    {
      onError: (e) => {
        somethingWrongNotification();

        return rejectWithValue(e);
      },
    },
  );

  return response.data;
});

export const playgroundRequest = createAsyncThunk(
  PLAYGROUND_REQUEST,
  async (values, { dispatch, getState, rejectWithValue }) => {
    const { fileText, assistant, message } = values;
    const threadId = getState().aiPlayground.threadId;
    const constructedMessage = (message + ' ' + String(fileText)).trim();

    if (!assistant || !message) {
      return rejectWithValue(new Error('Assistant and message are required'));
    }

    if (!threadId) {
      await initPlayground({ assistant, message: constructedMessage }, dispatch);
    } else {
      await replyPlayground({ assistant, message: constructedMessage }, dispatch);
    }
  },
);

/**
 * INIT ASSISTANT PLAYGROUND
 */
export const initAssistantPlayground = createAsyncThunk(
  INIT_ASSISTANT_PLAYGROUND,
  async ({ assistant, message }, { rejectWithValue }) => {
    try {
      const response = await assistantStrategy.init(assistant, message);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const initChatPlayground = createAsyncThunk(
  INIT_CHAT_PLAYGROUND,
  async ({ assistant, message }, { rejectWithValue }) => {
    try {
      const response = await chatsStrategy.init(assistant, message);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const initMediaPlayground = createAsyncThunk(
  INIT_MEDIA_PLAYGROUND,
  async ({ assistant, message }, { rejectWithValue }) => {
    try {
      const response = await mediaStrategy.init(assistant, message);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const initPlayground = async ({ assistant, message }, dispatch) => {
  const strategy = getStrategy(assistant);

  if (!strategy) {
    console.error('No strategy found for assistant:', assistant);
    return;
  }

  if (assistantStrategy.supports(assistant)) {
    await dispatch(initAssistantPlayground({ assistant, message }));
  } else if (chatsStrategy.supports(assistant)) {
    await dispatch(initChatPlayground({ assistant, message }));
  } else if (mediaStrategy.supports(assistant)) {
    await dispatch(initMediaPlayground({ assistant, message }));
  }
};

/**
 * REPLY ASSISTANT PLAYGROUND
 */
export const replyAssistantPlayground = createAsyncThunk(
  REPLY_ASSISTANT_PLAYGROUND,
  async ({ reply, assistant }, { rejectWithValue, getState }) => {
    const threadId = getState().aiPlayground.threadId;

    if (!assistant || !threadId) {
      return rejectWithValue(null);
    }

    const response = await assistantStrategy.reply(assistant, reply, threadId);

    return response;
  },
);

export const replyAiChatPlayground = createAsyncThunk(
  REPLY_AI_CHAT_PLAYGROUND,
  async ({ reply, assistant }, { rejectWithValue, getState }) => {
    const threadId = getState().aiPlayground.threadId;

    if (!assistant || !threadId) {
      return rejectWithValue(null);
    }

    const response = await chatsStrategy.reply(assistant, reply, threadId);

    return response;
  },
);

const replyPlayground = async ({ assistant, message }, dispatch) => {
  const strategy = getStrategy(assistant);

  if (!strategy) {
    console.error('No strategy found for assistant:', assistant);
    return;
  }

  if (assistantStrategy.supports(assistant)) {
    await dispatch(replyAssistantPlayground({ reply: message, assistant }));
  } else if (chatsStrategy.supports(assistant)) {
    await dispatch(replyAiChatPlayground({ reply: message, assistant }));
  } else if (mediaStrategy.supports(assistant)) {
    await dispatch(initMediaPlayground({ assistant, message }));
  }
};
